import type { ApiResponse, GetListResponse } from './generic';

export type SurveyStatus = 'draft' | 'published' | 'closed';

export type SurveyAudienceScope =
  'none' | 'all' | 'org' | 'employee' | 'new_hire';

/** How often a survey re-issues itself. */
export type SurveyFrequency = 'one_time' | 'monthly' | 'quarterly' | 'yearly';

/**
 * When an individual employee receives it.
 *
 * `random` gives each employee their own delivery date inside the cycle, so a
 * yearly survey reaches the company spread across the year rather than all at
 * once. It needs a recurring frequency to have a period to spread across.
 * `after_joining` is the new-hire mode and is always one-time.
 */
export type SurveyTiming = 'now' | 'scheduled' | 'random' | 'after_joining';

export type SurveyCompletionWindowMode = 'unlimited' | 'fixed';

/** The timing choices a given frequency actually allows. */
export function timingOptionsFor(frequency: SurveyFrequency): SurveyTiming[] {
  return frequency === 'one_time'
    ? ['now', 'scheduled']
    : ['now', 'scheduled', 'random'];
}

export interface Survey {
  id: string;
  name: string;
  description?: string;
  status: SurveyStatus;
  audienceScope?: SurveyAudienceScope;
  isAnonymous: boolean;
  questionsCount?: number;
  responsesCount?: number;
  responseRate?: number; // percentage, 0-100
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
  startDate?: string;
  endDate?: string;
  frequency?: SurveyFrequency;
  timing?: SurveyTiming;
  completionWindowDays?: number | null;
  daysAfterJoining?: number | null;
  cycleNumber?: number;
  publishedAt?: string;
  closedAt?: string;
  /** Survey trends on (default) or off (spec 2026-10-01, 2.12). */
  trendsEnabled?: boolean;
  /** Set when duplicated with "Compare with the original" (D83). */
  trendSeriesId?: string | null;
  /** Weekly AI summary of written answers (F2-P2, D77): on by default. */
  aiDigestEnabled?: boolean;
}

export interface SurveySummary {
  totalSurveys: number;
  published: number;
  drafts: number;
  closed: number;
  avgResponseRate: number;
  totalSubmissions: number;
}

export interface GetSurveysParams {
  search?: string;
  status?: SurveyStatus | '';
  audienceScope?: SurveyAudienceScope | '';
  page?: number;
  limit?: number;
  sortBy?: 'name' | 'createdAt' | 'updatedAt' | 'responseRate' | 'status';
  sortOrder?: 'ASC' | 'DESC';
}

export type GetSurveysResponse = GetListResponse<Survey>;

export type GetSurveySummaryResponse = ApiResponse<SurveySummary>;

export type GetSurveyDetailResponse = ApiResponse<Survey>;

// ─── Real API wire shapes (Survey CRUD) ────────────────────────────────────
export interface SurveyApiEntity {
  id: string;
  title: string;
  description?: string | null;
  status: SurveyStatus;
  audienceScope: SurveyAudienceScope;
  isAnonymous: boolean;
  startsOn: string;
  endsOn?: string | null;
  frequency?: SurveyFrequency;
  timing?: SurveyTiming;
  completionWindowDays?: number | null;
  daysAfterJoining?: number | null;
  cycleNumber?: number;
  publishedAt?: string | null;
  closedAt?: string | null;
  createdByUserId?: string | null;
  createdByUser?: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
  createdAt: string;
  updatedAt: string;
  trendsEnabled?: boolean;
  trendSeriesId?: string | null;
  aiDigestEnabled?: boolean;
}

export interface SurveyDashboardStats {
  total: number;
  draft: number;
  published: number;
  closed: number;
  averageResponseRate: number;
}

// ─── Payloads (used by later wizard steps too) ─────────────────────────────

export interface CreateSurveyPayload {
  name: string;
  description?: string;
  isAnonymous: boolean;
  startDate: string;
  /** Optional: a recurring or new-hire survey can run open-ended. */
  endDate?: string;
  frequency?: SurveyFrequency;
  timing?: SurveyTiming;
  /** Days from each recipient's own delivery. Omit for unlimited. */
  completionWindowDays?: number | null;
  /** New-hire surveys only. */
  daysAfterJoining?: number | null;
  // Derived from the wizard's assignment step (all_employees->'all',
  // individuals->'employee', dept_designation->'org') — defaults to 'none'
  // when omitted.
  audienceScope?: SurveyAudienceScope;
}

// publish/close are dedicated endpoints, not part of the real update DTO —
// no `status` field here.
export type UpdateSurveyPayload = Partial<CreateSurveyPayload> & {
  trendsEnabled?: boolean;
  aiDigestEnabled?: boolean;
  /** Only `null`: unlinks the survey from its trend series. */
  trendSeriesId?: null;
};

// ─── Question Types ─────────────────────────────────────────────────────────

export enum QuestionType {
  SHORT_TEXT = 'short_text',
  PARAGRAPH = 'paragraph',
  NUMBER = 'number',
  EMAIL = 'email',
  PHONE = 'phone',
  DATE = 'date',
  TIME = 'time',
  SINGLE_CHOICE = 'single_choice',
  MULTIPLE_CHOICE = 'multiple_choice',
  DROPDOWN = 'dropdown',
  YES_NO = 'yes_no',
  RATING = 'rating',
  LINEAR_SCALE = 'linear_scale',
  FILE_UPLOAD = 'file_upload',
  IMAGE_UPLOAD = 'image_upload',
  IMAGE_CHOICE = 'image_choice',
}

export interface QuestionOption {
  id: string;
  label: string;
  description?: string;
  imageUrl?: string; // image_choice only
}

export interface TextConfig {
  maxCharacters?: number;
}

export interface DateConfig {
  minDate?: string;
  maxDate?: string;
}

export interface NumberQuestionConfig {
  minValue?: number;
  maxValue?: number;
  defaultValue?: number;
  allowDecimal: boolean;
}

export interface FileUploadConfig {
  allowedFileTypes: string[]; // 'pdf' | 'doc' | 'docx' | 'xls' | 'xlsx' | 'ppt' | 'pptx' | 'csv' | 'txt' | 'zip' | 'png' | 'jpeg' | 'webp'
  maxFileSizeMb: number;
  maxFileCount: number;
  allowMultipleFiles: boolean;
}

export interface ImageUploadConfig {
  maxImageCount: number;
  maxFileSizeMb: number;
  allowedFormats: string[]; // 'png' | 'jpg' | 'jpeg' | 'webp'
}

export interface RatingConfig {
  maxRating: number; // e.g. 5, 10
}

export interface LinearScaleConfig {
  minValue: number;
  maxValue: number;
  minLabel?: string;
  maxLabel?: string;
}

export interface SurveyQuestion {
  id: string;
  type: QuestionType;
  title: string;
  description?: string;
  placeholder?: string;
  required: boolean;
  helpText?: string;
  order: number;
  options?: QuestionOption[]; // choice-based types
  numberConfig?: NumberQuestionConfig;
  fileUploadConfig?: FileUploadConfig;
  imageUploadConfig?: ImageUploadConfig;
  ratingConfig?: RatingConfig;
  linearScaleConfig?: LinearScaleConfig;
  textConfig?: TextConfig;
  dateConfig?: DateConfig;
  /** Trend direction; null or absent = the type's default (spec 2.2). */
  trendPolarity?: SurveyTrendPolarity | null;
  /** Single choice / dropdown: the options are an ordered scale. */
  trendOrdered?: boolean;
}

export interface SurveySection {
  id: string;
  title: string;
  description?: string;
  order: number;
  questions: SurveyQuestion[];
}

export interface SurveyPage {
  id: string;
  title: string;
  description?: string;
  order: number;
  sectionsEnabled: boolean;
  questions: SurveyQuestion[]; // used when sectionsEnabled === false
  sections: SurveySection[]; // used when sectionsEnabled === true
}

export interface SurveyBuilderState {
  pages: SurveyPage[];
}

// ─── Assignment ─────────────────────────────────────────────────────────────

export enum SurveyAssignmentType {
  ALL_EMPLOYEES = 'all_employees',
  DEPT_DESIGNATION = 'dept_designation', // Changed from separate 'departments' | 'designations'
  INDIVIDUALS = 'individuals',
  /** One or more branches — "location" in the UI. */
  LOCATION = 'location',
  /**
   * Nobody today: whoever joins from now on, each reached on their own
   * `daysAfterJoining` mark. Always one-time, always `after_joining` timing.
   */
  NEW_HIRES = 'new_hires',
}

export interface SurveyAssignmentTarget {
  type: SurveyAssignmentType;
  branchIds: string[];
  departmentIds: string[];
  designationIds: string[];
  employeeIds: string[];
}

export interface AssignmentOption {
  id: string;
  name: string;
  employeeCount?: number; // for departments/designations, shown in UI
}

export type GetSurveyAssignmentResponse = ApiResponse<SurveyAssignmentTarget>;
export type UpdateSurveyAssignmentResponse =
  ApiResponse<SurveyAssignmentTarget>;

// ─── Builder ────────────────────────────────────────────────────────────────

export type GetSurveyBuilderResponse = ApiResponse<SurveyBuilderState>;
export type UpdateSurveyBuilderResponse = ApiResponse<SurveyBuilderState>;

// ─── Wizard-wide form state ─────────────────────────────────────────────────
export interface SurveyBasicInfo {
  title: string;
  description?: string;
  isAnonymous: boolean;
  startDate?: string;
  endDate?: string;
  frequency: SurveyFrequency;
  timing: SurveyTiming;
  /**
   * Wizard-only: which half of the "time to complete" control is active.
   * Not sent to the API — `completionWindowDays` of null already means
   * unlimited there — but it has to live in the form so the dropdown can be
   * Formik-bound like every other field.
   */
  completionWindowMode: SurveyCompletionWindowMode;
  /** Null is a deliberate "unlimited", not a missing value. */
  completionWindowDays?: number | null;
  daysAfterJoining?: number | null;
}

export interface SurveyWizardFormValues {
  basicInfo: SurveyBasicInfo;
  builder: SurveyBuilderState;
  assignment: SurveyAssignmentTarget;
}

// ─── APPEND THESE to @gokrew/models/survey (same file as Survey, SurveyQuestion, etc.) ───

export interface SurveyStatistics {
  assignedCount: number;
  completedCount: number;
  pendingCount: number;
  inProgressCount: number;
  expiredCount: number;
  responseRate: number; // percentage, 0-100
  averageRating: number; // out of 5
  averageCompletionMinutes: number;
}

export interface AssignmentSummaryItem {
  id: string;
  name: string;
  employeeCount: number;
}

export interface SurveyAssignmentSummary {
  departments: AssignmentSummaryItem[];
  designations: AssignmentSummaryItem[];
  employeeIds: string[];
  individuallyAssignedCount: number;
  totalAssigned: number;
  isAllEmployees: boolean;
}

export interface ResponseProgress {
  completed: number;
  inProgress: number;
  pending: number;
  expired: number;
  total: number;
  percent: number;
}

export interface RatingDistributionItem {
  stars: number; // 5, 4, 3, 2, 1
  percent: number;
}

export interface DepartmentParticipation {
  departmentName: string;
  percent: number;
}

export interface ResponseTimelinePoint {
  label: string; // 'Week 1', 'Day 1', etc.
  count: number;
}

// Anonymous surveys: the analytics cover `includedCount` of `submittedCount`
// submissions and move in blocks of `updateEvery`.
export interface AnonymousResultsSnapshot {
  includedCount: number;
  submittedCount: number;
  updateEvery: number;
}

export interface SurveyAnalytics {
  averageRating: number;
  responseRate: number;
  completionRate: number;
  totalResponses: number;
  ratingDistribution: RatingDistributionItem[];
  departmentParticipation: DepartmentParticipation[];
  responseTimeline: ResponseTimelinePoint[];
  // Null for an identified survey, or an anonymous one still under the minimum.
  anonymousSnapshot: AnonymousResultsSnapshot | null;
}

export interface SurveyOverviewData {
  survey: Survey;
  statistics: SurveyStatistics;
  assignmentSummary: SurveyAssignmentSummary;
  responseProgress: ResponseProgress;
  analytics: SurveyAnalytics;
}

export type GetSurveyOverviewResponse = ApiResponse<SurveyOverviewData>;

// ─── Responses ──────────────────────────────────────────────────────────────

export enum SurveyResponseStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  EXPIRED = 'expired',
}

export interface SurveyResponseListItem {
  id: string;
  employeeId: string | null; // null when survey is anonymous
  employeeName: string | null; // null when survey is anonymous
  employeeEmail: string | null; // null when survey is anonymous
  department: string | null; // hidden (null) when anonymous, per spec
  branch: string | null; // hidden (null) when anonymous, per spec
  status: SurveyResponseStatus;
  startedAt: string | null;
  submittedAt: string | null;
  timeTakenMinutes: number | null;
}

export interface GetSurveyResponsesParams {
  search?: string; // searches employee name/email server-side
  status?: SurveyResponseStatus | '';
  page?: number;
  limit?: number;
}

export type GetSurveyResponsesResponse =
  GetListResponse<SurveyResponseListItem>;

export interface AdjacentSurveyResponses {
  previousId: string | null;
  nextId: string | null;
}

export type GetAdjacentSurveyResponsesResponse =
  ApiResponse<AdjacentSurveyResponses>;

export interface SurveyResponseAnswer {
  questionId: string;
  questionTitle: string;
  questionType: QuestionType;
  required: boolean;
  answer: string | string[] | number | boolean | null;
  attachments?: {
    fileName: string;
    url?: string; // pre-signed, temporary S3 GET URL — expires (~1hr)
    fileSize?: number;
    mimeType?: string;
  }[]; // file/image upload answers
}

// Same per-recipient identity/status fields as the list item, plus the
// full answer breakdown only the detail endpoint returns.
export interface SurveyResponseDetail extends SurveyResponseListItem {
  surveyId: string;
  surveyName: string;
  isAnonymous: boolean;
  answeredCount: number;
  totalQuestions: number;
  answers: SurveyResponseAnswer[];
}

export type GetSurveyResponseDetailResponse = ApiResponse<SurveyResponseDetail>;

// ─── Aggregate results (GET :surveyId/results) ──────────────────────────────
// The only answer view for an anonymous survey. The server withholds every
// answer-derived value until `minResponses` people have submitted, and
// written answers arrive shuffled with nothing attached.

export type SurveyResultKind =
  'choice' | 'boolean' | 'scale' | 'number' | 'text' | 'count_only';

export interface SurveyQuestionResult {
  questionId: string;
  title: string;
  // Raw backend type (e.g. rating_5); render by `kind`, not by this.
  type: string;
  kind: SurveyResultKind;
  answeredCount: number;
  options?: { id: string; label: string; count: number }[];
  yesCount?: number;
  noCount?: number;
  distribution?: { value: number; count: number }[];
  average?: number | null;
  textAnswers?: string[];
}

export interface SurveyResults {
  surveyId: string;
  isAnonymous: boolean;
  minResponses: number;
  submittedCount: number;
  // Submissions the numbers are computed over. For an anonymous survey the
  // first floor(n / 5) * 5, so results move in blocks of `updateEvery`.
  includedCount: number;
  updateEvery: number | null;
  resultsVisible: boolean;
  questions: SurveyQuestionResult[];
}

export type GetSurveyResultsResponse = ApiResponse<SurveyResults>;

// ─── Trends (spec 2026-10-01, Feature 2) ───────────────────────────────────

/** Which direction of a metric is adverse (spec 2.2). */
export type SurveyTrendPolarity =
  'higher_is_better' | 'lower_is_better' | 'neutral';

export const SURVEY_TREND_POLARITIES: SurveyTrendPolarity[] = [
  'higher_is_better',
  'lower_is_better',
  'neutral',
];

/** Rolling weeks, cycle over cycle, or run over run (spec 2.3). */
export type SurveyTrendMode = 'rolling' | 'cycle' | 'series';

export type SurveyTrendState =
  | 'not_enough_data'
  | 'stable'
  | 'watching'
  | 'drop_confirmed'
  | 'improving'
  | 'changed'
  | 'too_few';

export type SurveyTrendAnnotationType =
  | 'question_changed'
  | 'window_changed'
  | 'survey_closed'
  | 'alert_opened'
  | 'alert_resolved';

/** One period's aggregate; never a single answer (A3). Scores are 0-100. */
export interface SurveyTrendPoint {
  periodKey: string;
  windowStart: string;
  windowEnd: string;
  n: number;
  mean: number | null;
  ciLow: number | null;
  ciHigh: number | null;
  baselineMean: number | null;
  /** False when an anonymous point was carried forward (blocks of 5). */
  published: boolean;
}

export interface SurveyTrendAlert {
  id: string;
  status: 'open' | 'acknowledged' | 'resolved';
  fromMean: number;
  toMean: number;
  delta: number;
  n: number;
  weeks: number | null;
  openedPeriodKey: string;
  acknowledgedAt: string | null;
  /** The group carrying the change ("mostly Sales", F2-P3), if any. */
  concentration?: {
    segmentType: SurveyTrendSegmentKind;
    label: string;
  } | null;
}

export interface SurveyTrendMetric {
  /** `index` or the question's trend key. */
  key: string;
  kind: 'index' | 'question';
  questionId: string | null;
  title: string | null;
  /** The backend question type (`rating_5`, `linear_scale`, ...). */
  type: string | null;
  polarity: SurveyTrendPolarity;
  /** Ratings and linear scales: their own units, for the Scale view. */
  nativeScale: { min: number; max: number } | null;
  state: SurveyTrendState;
  latest: {
    n: number;
    mean: number | null;
    baselineMean: number | null;
    delta: number | null;
  } | null;
  points: SurveyTrendPoint[];
  alert: SurveyTrendAlert | null;
}

/** `GET company-surveys/:id/trends`. */
export interface SurveyTrends {
  enabled: boolean;
  mode: SurveyTrendMode;
  windowWeeks: number | null;
  baselineWeeks: number | null;
  state: SurveyTrendState;
  readiness: {
    n: number;
    min: number;
    etaWeeks: number | null;
    etaDate: string | null;
    needsRepeat: boolean;
  };
  isAnonymous: boolean;
  updateEvery: number | null;
  metrics: SurveyTrendMetric[];
  annotations: {
    periodKey: string;
    type: SurveyTrendAnnotationType;
    metric: string | null;
  }[];
}

/** `POST company-surveys/trends/forecast` (the setup estimate, spec 2.6). */
export interface SurveyTrendForecastPayload {
  frequency: SurveyFrequency;
  timing: SurveyTiming;
  startsOn?: string;
  audience?: {
    type: SurveyAssignmentType;
    branchIds?: string[];
    departmentIds?: string[];
    designationIds?: string[];
    employeeIds?: string[];
  };
}

export interface SurveyTrendForecast {
  mode: SurveyTrendMode;
  audienceSize: number;
  responseRate: number;
  responsesPerWeek: number;
  windowWeeks: number | null;
  visibleAfterWeeks: number | null;
  alertsAfterWeeks: number | null;
  cycleDate: string | null;
  feasible: boolean;
}

/** `GET company-surveys/:id/trends/digest` (F2-P2, spec 2.8). */
export interface SurveyTrendDigest {
  /** `ok`, or why there is none. */
  status: 'ok' | 'off' | 'not_enough' | 'failed' | 'none';
  periodKey: string | null;
  language: 'en' | 'fr' | null;
  content: {
    summary: string | null;
    themes: {
      label: string;
      sentiment: 'negative' | 'mixed' | 'positive';
      /** Approximate share of the answers, 0 to 1. */
      share: number;
      questions: string[];
    }[];
    alertExplanations: { metric: string; text: string; metricKey?: string }[];
    nextAction: string | null;
    /** The full digest was refused twice: only themes remain. */
    themesOnly?: boolean;
  } | null;
  answersUsed: number;
  answersTotal: number;
  createdAt: string | null;
}

/** Segment kinds of survey trends (F2-P3): departments, locations, teams. */
export type SurveyTrendSegmentKind = 'department' | 'branch' | 'manager';

export const SURVEY_TREND_SEGMENT_KINDS: SurveyTrendSegmentKind[] = [
  'department',
  'branch',
  'manager',
];

/** `GET company-surveys/:id/trends/segments` (groups of 5+ answers only). */
export interface SurveyTrendSegments {
  metric: string | null;
  segmentType: SurveyTrendSegmentKind;
  nativeScale: { min: number; max: number } | null;
  segments: {
    /** The group id, or `other`. */
    key: string;
    label: string | null;
    isOther: boolean;
    state: SurveyTrendState;
    latest: {
      n: number;
      mean: number | null;
      baselineMean: number | null;
      delta: number | null;
    } | null;
    points: SurveyTrendPoint[];
    alert: SurveyTrendAlert | null;
  }[];
}

/** `GET company-surveys/:id/trends/shares`: choice questions over time. */
export interface SurveyTrendShare {
  questionId: string;
  title: string;
  type: string;
  options: { id: string; label: string }[];
  points: {
    periodKey: string;
    windowStart: string;
    windowEnd: string;
    n: number;
    /** Option id to share of the answering respondents, 0 to 1. */
    shares: Record<string, number>;
  }[];
}
